import {CommonModule} from '@angular/common';
import {MatDialogModule} from '@angular/material/dialog';
import {Component, OnInit, signal} from '@angular/core';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {TranslateService, TranslateModule} from '@ngx-translate/core';
import {
  ContentLoaderComponent,
  DialogCloseButtonComponent,
  IconButtonComponent
} from '@q9elements/ui-kit/components';
import {VersionsService} from '../../../../core/services/versions.service';
import {SpinnerService} from '../../../../core/spinner/spinner.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../models/diagram-version.constant';
import {
  AUTHORISATION_ACTION_CONFIG,
  AUTHORISATION_HISTORY_ACTION
} from '../../models/authorisation-history.model';
import {VersionNamePipe} from '../../pipes/version-name.pipe';
import {VersionHistoryTreeComponent} from './version-history-tree/version-history-tree.component';
import {versionHistoryLink} from 'src/app/shared/constants/elements-links';
import {ConfigService} from '@q9elements/ui-kit/common';

@Component({
  selector: 'app-versions-history-popup',
  templateUrl: './versions-history-popup.component.html',
  styleUrls: ['./versions-history-popup.component.scss'],
  providers: [SpinnerService],
  standalone: true,
  imports: [
    CommonModule,
    DialogCloseButtonComponent,
    IconButtonComponent,
    ContentLoaderComponent,
    MatDialogModule,
    VersionHistoryTreeComponent,
    MatTooltipModule,
    TranslateModule,
    VersionNamePipe
  ]
})
export class VersionsHistoryPopupComponent implements OnInit {
  public versionsHistory$: Observable<any>;
  public stateTransformObject;
  public chosenVersionHistoryItem;
  public isLoading = signal(false);
  public noItemsImage = `${ConfigService.environment.STATIC_FILES_URL}/icons/placeholders/empty.svg`;

  constructor(
    protected versionsSvc: VersionsService,
    private spinnerSvc: SpinnerService,
    private translateSvc: TranslateService
  ) {}

  ngOnInit() {
    this.stateTransformObject = {
      [DIAGRAM_VERSION_STATES_ENUM.NEVER_PUBLISHED]: this.translateSvc.instant(
        'VERSIONING.DIAGRAM_STATE_NEVER_PUBLISHED'
      ),
      [DIAGRAM_VERSION_STATES_ENUM.READY]: this.translateSvc.instant(
        'VERSIONING.DIAGRAM_STATE_READY'
      )
    };
    this.getVersionsHistory();
    this.versionsHistory$ = this.versionsSvc.versionsHistory$.pipe(
      map(versionHistory => this.sortVersions(versionHistory)),
      map(versionHistory => this.mergeHistory(versionHistory))
    );
  }

  get sortConfig() {
    return {
      [DIAGRAM_VERSION_STATES_ENUM.CANCELLED]: 3,
      [DIAGRAM_VERSION_STATES_ENUM.READY]: 1,
      [DIAGRAM_VERSION_STATES_ENUM.BEING_AUTHORISED]: 0,
      [DIAGRAM_VERSION_STATES_ENUM.NEVER_PUBLISHED]: 4,
      [DIAGRAM_VERSION_STATES_ENUM.MASTER]: 2,
      [DIAGRAM_VERSION_STATES_ENUM.ARCHIVE]: 2
    };
  }

  public goToArticle() {
    window.open(versionHistoryLink);
  }

  public putReadyAtTheBeginning(versionHistory) {
    const history = versionHistory.filter(item => item.state !== DIAGRAM_VERSION_STATES_ENUM.DRAFT);

    if (history.length) {
      const last = history.pop();

      if (last.state === DIAGRAM_VERSION_STATES_ENUM.READY) {
        history.unshift(last);
      } else {
        history.push(last);
      }

      this.chosenVersionHistoryItem = history[0];
    }

    return history;
  }

  public sortVersions(versionHistory) {
    const history = versionHistory.filter(item => item.state !== DIAGRAM_VERSION_STATES_ENUM.DRAFT);

    if (history.length) {
      const sortConfig = this.sortConfig;

      history.sort(this.sortVersionItems(sortConfig));
      this.chosenVersionHistoryItem = history[0];
    }

    return history;
  }

  public mergeHistory(versionHistory) {
    const {REQUESTED_AUTHORISATION, MADE_READY} = AUTHORISATION_HISTORY_ACTION;

    const setConfigOnAuthItem = item => {
      item.config = AUTHORISATION_ACTION_CONFIG[item.action];

      return item;
    };

    const filterAuth = item => ![REQUESTED_AUTHORISATION, MADE_READY].includes(item.action);

    return versionHistory.map(item => {
      const requestHistoryExist = !!item.requestHistory.length;
      const requestHistory = item.requestHistory.filter(filterAuth);

      item.requestHistory = requestHistory.map(setConfigOnAuthItem);

      item.fullHistory = [...item.history, ...item.requestHistory];

      if (requestHistoryExist) {
        item.fullHistory = item.fullHistory.sort(this.sortHistoryItems());
      }

      return item;
    });
  }

  public async getVersionsHistory() {
    this.isLoading.set(true);
    await this.versionsSvc.getVersionsHistory();
    this.isLoading.set(false);
  }

  public changeChosenVersionHistoryItem(versionHistoryItem) {
    this.chosenVersionHistoryItem = versionHistoryItem;
  }

  public sortHistoryItems() {
    return (a, b) => {
      switch (true) {
        case a.what === DIAGRAM_VERSION_STATES_ENUM.BEING_AUTHORISED:
          return -1;

        case b.what === DIAGRAM_VERSION_STATES_ENUM.BEING_AUTHORISED:
          return 1;

        case a.isAuthHistory && b.isAuthHistory:
          return a.when - b.when;

        case a.isAuthHistory:
          return -1;

        case b.isAuthHistory:
          return 1;

        default:
          return a.when - b.when;
      }
    };
  }

  public sortVersionItems(config) {
    return (a, b) => {
      switch (true) {
        case config[a.state] === config[b.state]:
          return b.version - a.version;

        default:
          return config[a.state] - config[b.state];
      }
    };
  }
}
